import { insightFeatureId } from './insightMapJoin';
import { ICasinoReport, ICasinoSchema } from 'appRedux/models/casinoModels';
import { IListResponse, ISlotResponse } from '@interfaces/services';
import { Observable, Subscription, defer, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { getBankName } from 'common/common';
import { displayIdForInsightMnum } from 'lib/machineCatalog';
import {
  getInsightDataPeriodStart,
  getInsightResponseUpdateDate,
  isUpdateDateInCurrentPeriod,
} from 'lib/insightSummaryPeriod';
import {
  isInsightSummaryCacheAvailable,
  isInsightSummaryCacheFresh,
  readInsightSummaryCacheWithBudget,
  writeInsightSummaryCacheAsync,
} from 'lib/insightSummaryPersistentCache';

export { getMachineDisplayId } from 'lib/machineDisplayId';
export {
  displayIdForInsightMnum,
  buildMachinesByMnum,
  setActiveMachinesCatalog,
} from 'lib/machineCatalog';
// Jackpot insights excluded from the Coldest card edge (kept local after reverting !2118).
const JACKPOT_INSIGHTS = ['jackpot_value', 'jackpots', 'jackpot_value_per_spin', 'pop'];

/**
 * casinoInsight routing for the PWA.
 *
 * The switch keys ONLY off the active casino object's `insightInstance` field
 * (Mongo `casinos`, passed through GetAllCasinos/GetCasinoInfo). There are no
 * hardcoded casino ids here or anywhere else. Casinos without the field keep
 * the existing routes and behavior untouched.
 */

let activeCasino: Pick<ICasinoSchema, 'casinoId' | 'kpCasinoPk' | 'insightInstance'> | null = null;

/** Called from the redux store whenever the selected casino changes. */
export const setActiveInsightCasino = (casino?: Partial<ICasinoSchema> | null): void => {
  activeCasino = casino
    ? {
        casinoId: Number(casino.casinoId),
        kpCasinoPk: Number(casino.kpCasinoPk),
        insightInstance: casino.insightInstance ?? null,
      }
    : null;
};

const hasInstance = (n: unknown): boolean => n !== null && n !== undefined && String(n) !== '';

/** True when the casino object has insightInstance set. */
export const isInsightCasinoSchema = (casino?: Partial<ICasinoSchema> | null): boolean =>
  !!casino && hasInstance(casino.insightInstance);

/**
 * True when `kpCasinoPk` is the active casino and that casino object carries
 * insightInstance. Every report call in the PWA sends kpCasinoPk (casinofk /
 * casinoFk), so only that one id type is compared; a casinoId that happens to
 * equal another casino's kpCasinoPk can never match.
 */
export const isInsightCasino = (kpCasinoPk?: number | string | null): boolean => {
  if (!activeCasino || !hasInstance(activeCasino.insightInstance)) return false;
  if (kpCasinoPk === undefined || kpCasinoPk === null || String(kpCasinoPk) === '') return false;
  return Number(kpCasinoPk) === activeCasino.kpCasinoPk;
};

/** kpCasinoPk -> the casinoId (Mongo id) the GetCasinoInsight* routes expect. */
export const insightCasinoId = (kpCasinoPk: number | string): number =>
  activeCasino && Number(kpCasinoPk) === activeCasino.kpCasinoPk
    ? activeCasino.casinoId
    : Number(kpCasinoPk);

/** Legacy numeric insight ids (config/Insights.ts InsightIdEnum) -> insight keys. */
const INSIGHT_ID_TO_KEY: Record<number, string> = {
  1: 'money_won',
  2: 'spins',
  3: 'avg_money_won_per_spin',
  4: 'win_rate',
  5: 'jackpot_value',
  6: 'jackpots',
  7: 'money_played',
  8: 'return',
  9: 'sleeper_slot_rating',
  10: 'volatility_rating',
  11: 'pop',
  12: 'jackpot_value_per_spin',
  13: 'non_jkpt_won_per_spin',
};

/** '{1,2,8}' | 'spins' | 'spins,return' -> 'money_won,spins,return' (undefined = all). */
export const toInsightKeyList = (filter?: string | number | null): string | undefined => {
  if (filter === undefined || filter === null || `${filter}`.trim() === '') return undefined;
  const parts = `${filter}`
    .replace(/[{}]/g, '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => (/^[0-9]+$/.test(p) ? INSIGHT_ID_TO_KEY[Number(p)] : p))
    .filter((p): p is string => !!p);
  return parts.length ? Array.from(new Set(parts)).join(',') : undefined;
};

export const toInsightKey = (insight?: string | number | null): string | undefined =>
  toInsightKeyList(insight)?.split(',')[0];

/** Row returned by GetCasinoInsights (already normalized server-side). */
export interface IInsightRow {
  mnum: string | null;
  /** Alias of floorLocation (placement only). May be alphanumeric e.g. '2A5'. Not the display id. */
  slot_number: number | string | null;
  location_string?: string | null;
  floorLocation?: string | null;
  area: string | null;
  section: string | null;
  bank: string | null;
  location: string | null;
  onMap: boolean;
  game_name: string | null;
  [key: string]: unknown;
}

export interface IInsightSummaryResponse {
  status: string;
  updateDate?: string | null;
  stale?: boolean;
  totalCount: number;
  DataSet: IInsightRow[];
}

/**
 * Placement for insight casinos comes from the GetCasinoInsights rows (never
 * GetMapMetaData). `floorLocation` is the machine's floor location string.
 */
export const withPlacement = (row: IInsightRow) => {
  // floorLocation = machines.floorLocation (placement). slot_number is the same alias.
  // Never recompute from area/section/location. Prefer API floorLocation, then
  // location_string (legacy name), then slot_number.
  // DISPLAY id: prefer adaptDoc-joined machines.slotNumber on the row; else
  // GetSearchData catalog (displayIdForInsightMnum); else mnum.
  const pickFloor = (...vals: unknown[]): string | null => {
    for (const v of vals) {
      if (v === null || v === undefined) continue;
      const s = String(v).trim();
      if (s) return s;
    }
    return null;
  };
  const floor = pickFloor(row.floorLocation, row.location_string, row.slot_number);
  return {
    ...row,
    // Marks a casinoInsight row so the EGM card reads display id / section+bank.
    isInsightRow: true as const,
    // game_name / title come from adaptDoc machines join; blank when missing.
    game_name: textOrBlank(row.game_name),
    title: textOrBlank(row.title),
    mnum: row.mnum == null || String(row.mnum).trim() === '' ? null : String(row.mnum).trim(),
    floorLocation: floor,
    slot_number: floor,
    location_string: floor,
    onMap: row.onMap === true,
  };
};

// One GetCasinoInsights request per casino+timeframe feeds the map, the list
// and Details (in-memory shareReplay dedupe, 60s, matching the API's Cache-Control).
// Persistent IndexedDB cache adds :05/:35 period freshness (see insightSummaryPeriod).
//
// IMPORTANT: the returned observable MUST complete after the settled response.
// MapStats clears list/map spinners only on complete/error — a long-lived
// BehaviorSubject that never completes leaves Explore spinning forever (Red Wind).
// IDB read/write are best-effort and never block delivering the network response.
const SUMMARY_TTL_MS = 60_000;
/** Backoff while the pipeline's updateDate lags behind the open period. */
export const INSIGHT_SUMMARY_STALE_RETRY_MS = 150_000;

type SummaryMemoryEntry = {
  at: number;
  obs: Observable<IInsightSummaryResponse>;
  last: IInsightSummaryResponse | null;
  refresh: (force?: boolean) => void;
  dispose: () => void;
};

const summaryCache = new Map<string, SummaryMemoryEntry>();

const normalizeSummaryResponse = (
  resp: IInsightSummaryResponse | void
): IInsightSummaryResponse =>
  ({
    ...(resp || { status: 'ok', totalCount: 0 }),
    DataSet: ((resp && resp.DataSet) || []).map(withPlacement),
  } as IInsightSummaryResponse);

const responseIsCurrentPeriod = (resp: IInsightSummaryResponse, now = new Date()): boolean =>
  isUpdateDateInCurrentPeriod(getInsightResponseUpdateDate(resp), now);

export const sharedInsightSummary = (
  casinoId: number,
  timeframe: number,
  fetcher: () => Observable<IInsightSummaryResponse | void>
): Observable<IInsightSummaryResponse> => {
  const key = `${casinoId}:${timeframe}`;
  const hit = summaryCache.get(key);
  if (hit && Date.now() - hit.at < SUMMARY_TTL_MS) return hit.obs;

  hit?.dispose();

  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retrySub: Subscription | null = null;
  let disposed = false;

  const clearRetry = () => {
    if (retryTimer != null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    retrySub?.unsubscribe();
    retrySub = null;
  };

  const persistAsync = (resp: IInsightSummaryResponse) => {
    // Fire-and-forget IndexedDB write — never await on the emit path.
    try {
      writeInsightSummaryCacheAsync(casinoId, timeframe, resp);
    } catch {
      /* ignore */
    }
  };

  const runFetch = (): Observable<IInsightSummaryResponse> =>
    fetcher().pipe(
      map((resp) => normalizeSummaryResponse(resp)),
      tap((resp) => persistAsync(resp))
    );

  const scheduleRetryIfStale = (resp: IInsightSummaryResponse) => {
    if (disposed || responseIsCurrentPeriod(resp)) {
      clearRetry();
      return;
    }
    clearRetry();
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (disposed) return;
      retrySub = runFetch().subscribe({
        next: (fresh) => {
          const mem = summaryCache.get(key);
          if (mem) {
            mem.last = fresh;
            mem.at = Date.now();
            // Replace replay for NEW subscribers; in-flight UI already completed.
            mem.obs = of(fresh).pipe(shareReplay({ bufferSize: 1, refCount: false }));
          }
          scheduleRetryIfStale(fresh);
        },
        error: () => {
          scheduleRetryIfStale(resp);
        },
      });
    }, INSIGHT_SUMMARY_STALE_RETRY_MS);
  };

  // IDB read is budgeted; on miss/timeout/unavailable we go network-only.
  // Period-fresh cache can skip the network; stale cache emits then revalidates.
  // The observable always completes so Explore loading flags clear.
  // When IDB is unavailable (jsdom / private mode / blocked), take the sync
  // network-only path so callers still get next+complete in the same turn.
  const source$ = defer(() => {
    if (!isInsightSummaryCacheAvailable()) {
      return runFetch().pipe(
        tap((resp) => scheduleRetryIfStale(resp)),
        catchError((err) => {
          summaryCache.delete(key);
          throw err;
        })
      );
    }

    return new Observable<IInsightSummaryResponse>((subscriber) => {
      let networkSub: Subscription | null = null;
      let finished = false;
      let cacheEmitted = false;

      const finish = () => {
        if (finished) return;
        finished = true;
        subscriber.complete();
      };

      const fail = (err: unknown) => {
        if (finished) return;
        if (cacheEmitted) {
          finish();
          return;
        }
        finished = true;
        summaryCache.delete(key);
        subscriber.error(err);
      };

      const startNetwork = () => {
        if (networkSub || finished) return;
        networkSub = runFetch().subscribe({
          next: (resp) => {
            subscriber.next(resp);
            scheduleRetryIfStale(resp);
          },
          error: (err) => fail(err),
          complete: () => finish(),
        });
      };

      let cancelled = false;
      readInsightSummaryCacheWithBudget(casinoId, timeframe)
        .then((cached) => {
          if (cancelled || finished) return;
          if (cached?.response && isInsightSummaryCacheFresh(cached)) {
            const normalized = normalizeSummaryResponse(cached.response);
            cacheEmitted = true;
            subscriber.next(normalized);
            finish();
            return;
          }
          if (cached?.response) {
            cacheEmitted = true;
            subscriber.next(normalizeSummaryResponse(cached.response));
          }
          startNetwork();
        })
        .catch(() => {
          if (cancelled || finished) return;
          startNetwork();
        });

      return () => {
        cancelled = true;
        networkSub?.unsubscribe();
      };
    });
  });

  const obs = source$.pipe(shareReplay({ bufferSize: 1, refCount: false }));

  const entry: SummaryMemoryEntry = {
    at: Date.now(),
    obs,
    last: null,
    refresh: (force = true) => {
      if (disposed) return;
      if (!force && entry.last && responseIsCurrentPeriod(entry.last)) return;
      entry.at = 0;
      summaryCache.delete(key);
      sharedInsightSummary(casinoId, timeframe, fetcher).subscribe({ error: () => undefined });
    },
    dispose: () => {
      disposed = true;
      clearRetry();
    },
  };

  obs.subscribe({
    next: (resp) => {
      entry.last = resp;
    },
    error: () => undefined,
  });

  summaryCache.set(key, entry);
  return obs;
};

export const clearInsightSummaryCache = (): void => {
  summaryCache.forEach((e) => e.dispose());
  summaryCache.clear();
};

/** Revalidate in-memory entries whose updateDate is outside the open period. */
export const refreshStaleInsightSummaries = (): void => {
  summaryCache.forEach((e) => e.refresh(false));
};

let lastForegroundPeriodStartMs: number | null = null;
let visibilityBound = false;

/** When the app returns to the foreground across a :05/:35 boundary, revalidate. */
export const bindInsightSummaryForegroundRefresh = (): void => {
  if (visibilityBound || typeof document === 'undefined') return;
  visibilityBound = true;
  lastForegroundPeriodStartMs = getInsightDataPeriodStart(new Date()).getTime();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const periodStart = getInsightDataPeriodStart(new Date()).getTime();
    if (lastForegroundPeriodStartMs != null && periodStart === lastForegroundPeriodStartMs) return;
    lastForegroundPeriodStartMs = periodStart;
    // Snapshot entries — refresh() mutates the map.
    Array.from(summaryCache.values()).forEach((e) => {
      e.at = 0;
      e.refresh(true);
    });
  });
};

// Bind once for the PWA session (safe in SSR / tests: no-ops without document).
bindInsightSummaryForegroundRefresh();

const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Sort by the insight value; `desc` = highest first (UI convention). Nulls last. */
export const sortRowsByInsight = <T extends Record<string, unknown>>(
  rows: T[],
  insight: string | undefined,
  orderby: string | undefined
): T[] => {
  if (!insight) return rows.slice();
  const desc = `${orderby || 'desc'}`.toLowerCase() !== 'asc';
  return rows.slice().sort((a, b) => {
    const va = numOrNull(a[insight]);
    const vb = numOrNull(b[insight]);
    if (va === null && vb === null) return Number(a.slot_number ?? 0) - Number(b.slot_number ?? 0);
    if (va === null) return 1;
    if (vb === null) return -1;
    if (va === vb) return Number(a.slot_number ?? 0) - Number(b.slot_number ?? 0);
    return desc ? vb - va : va - vb;
  });
};

export const parseSlotList = (raw?: string | null): Set<string> | null => {
  if (!raw || `${raw}`.trim() === '' || `${raw}`.trim() === 'all') return null;
  const list = `${raw}`
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return list.length ? new Set(list) : null;
};

/**
 * Wraps rows in the legacy IListResponse envelope. IResponse<T> requires
 * response/code fields the report routes never send, so the one cast lives here.
 */
export const listResponse = <T>(
  DataSet: T[],
  lastUpdated = '',
  totalCount = DataSet.length
): IListResponse<T> =>
  ({
    status: 'ok',
    DataSet,
    totalCount,
    lastUpdated,
    Metadata: { recordCount: totalCount },
  } as unknown as IListResponse<T>);

/** Same envelope for slot-detail responses. */
export const slotResponse = <T>(DataSet: T[], lastUpdated = ''): ISlotResponse<T> =>
  ({
    status: 'ok',
    DataSet,
    slotPlayData: [],
    totalJackpots: [],
    lastUpdated,
    Metadata: { recordCount: DataSet.length },
  } as unknown as ISlotResponse<T>);

/** Insight rows carry the ICasinoReport fields the list/map read (numbers, not strings). */
export const asReportRows = (rows: IInsightRow[]): ICasinoReport[] =>
  rows as unknown as ICasinoReport[];

export const emptyList = <T>(): Observable<IListResponse<T> | void> => of(listResponse<T>([]));

/** True when a GetGeoJSON response carries at least one floor-plan feature. */
export const hasFloorPlanFeatures = (
  resp?: { DataSet?: Array<{ geojson?: { features?: unknown[] } }> } | null
): boolean => !!resp?.DataSet?.some((fp) => (fp?.geojson?.features?.length ?? 0) > 0);

/**
 * Whether a list row may offer "show on map" (fly-to / map focus). Machines
 * with onMap:false are list-only, and nothing is shown on a mapless Explore.
 */
export const canShowOnMap = (row: object | null | undefined, isMapless: boolean): boolean =>
  // Insight rows must explicitly say onMap === true; anything else is list-only.
  // Rows marked isInsightRow also need a resolved (unambiguous) floor-plan feature.
  !isMapless &&
  !!row &&
  (row as { onMap?: unknown }).onMap === true &&
  (!isInsightCardRow(row) || insightFeatureId(row) !== undefined);

/** Game name for display: blank when missing; never "null" or the mnum. */
export const displayGameName = (name: unknown, mnum?: unknown): string => {
  if (name === null || name === undefined) return '';
  const s = String(name).trim();
  if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return '';
  if (mnum !== null && mnum !== undefined && s === String(mnum).trim()) return '';
  return s;
};

// ---------- EGM card fields ----------
// Legacy rows: number = slot_number, bank = getBankName(area, section).
// casinoInsight rows: DISPLAY number = machines.slotNumber from the same adaptDoc
// machines join that supplies game_name/title; else GetSearchData catalog; else mnum.
// API / selection / favorites use mnum. floorLocation / slot_number stay placement-only.

// `any` index so interfaces like IGameData/ICasinoReport are accepted.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CardRow = { [key: string]: any } | null | undefined;

const textOrBlank = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

const part = (v: unknown): string => (v === null || v === undefined ? '' : String(v).trim());

export const isInsightCardRow = (row: CardRow): boolean => !!row && row.isInsightRow === true;

/**
 * Numeric EGM id for Details / favorites / playlist / compare / map-focus APIs.
 * Insight casinos: mnum (never floorLocation / slot_number — those are placement-only).
 * Legacy: slot_number.
 */
export const egmDetailId = (row: CardRow): number | null => {
  if (!row) return null;
  const raw = isInsightCardRow(row) ? row.mnum : row.slot_number;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  return Number.isFinite(n) ? n : null;
};

/** String form of egmDetailId for favorites/playlist keys ('' when unknown). */
export const egmDetailKey = (row: CardRow): string => {
  const id = egmDetailId(row);
  return id === null ? '' : String(id);
};

/** True when row is the EGM identified by detail id (activeSlotNumber / favorite slotNumber). */
export const matchesEgmDetailId = (
  row: CardRow,
  id: number | string | null | undefined
): boolean => {
  if (id === null || id === undefined || id === '') return false;
  if (!row) return false;
  if (isInsightCardRow(row)) {
    const m = part(row.mnum);
    if (!m) return false;
    return m === String(id).trim() || Number(m) === Number(id);
  }
  return Number(row.slot_number) === Number(id);
};

/**
 * Pick one EGM from a GetCasinoInsights DataSet by mnum (egmDetailId).
 * Used by Details to reuse Explore's casinoId+timeframe cache without a second list fetch.
 */
export const pickInsightSummaryRow = <T extends { mnum?: unknown }>(
  dataSet: T[] | null | undefined,
  mnum: number | string | null | undefined
): T | null => {
  if (mnum === null || mnum === undefined || mnum === '' || !Array.isArray(dataSet)) return null;
  return dataSet.find((r) => matchesEgmDetailId(r, mnum)) ?? null;
};

/**
 * Detail id from a search/catalog row for SelectedSlots / title-theme filters.
 * Insight search rows carry mnum — use that (never floorLocation/slot_number).
 * Legacy rows have no mnum; fall back to numeric slot_number.
 */
export const searchRowDetailId = (row: {
  mnum?: unknown;
  slot_number?: unknown;
} | null | undefined): number | null => {
  if (!row) return null;
  if (row.mnum !== null && row.mnum !== undefined && String(row.mnum).trim() !== '') {
    const n = Number(String(row.mnum).trim());
    return Number.isFinite(n) ? n : null;
  }
  if (row.slot_number === null || row.slot_number === undefined || row.slot_number === '') return null;
  const n = Number(row.slot_number);
  return Number.isFinite(n) ? n : null;
};

type SearchSelectionItem = {
  mnum?: unknown;
  slot_number?: unknown;
  total?: number;
  slots?: Array<{ mnum?: unknown; slot_number?: unknown }>;
  other_sub_games?: Array<{
    slots?: Array<{ mnum?: unknown; slot_number?: unknown }>;
    mnum?: unknown;
    slot_number?: unknown;
  }>;
} | null | undefined;

/**
 * Detail id for opening EGM Details from a Search result tap (Asset / Floor /
 * game child / group). Prefers group-level mnum (Asset rows), else the sole
 * distinct mnum among nested slots. Returns null when the tap is a multi-EGM
 * filter (more than one machine) — caller should use SelectedSlots instead.
 * Never returns floorLocation / alphanumeric placement codes.
 */
export const searchSelectionDetailId = (item: SearchSelectionItem): number | null => {
  if (!item) return null;
  const direct = searchRowDetailId(item);
  if (direct !== null) return direct;

  const rows: Array<{ mnum?: unknown; slot_number?: unknown }> = [];
  if (Array.isArray(item.slots) && item.slots.length > 0) {
    rows.push(...item.slots);
  } else if (Array.isArray(item.other_sub_games)) {
    for (const el of item.other_sub_games) {
      if (Array.isArray(el?.slots) && el.slots.length > 0) rows.push(...el.slots);
      else if (el) rows.push(el);
    }
  }

  const ids = Array.from(
    new Set(
      rows
        .map((r) => searchRowDetailId(r))
        .filter((n): n is number => n !== null)
    )
  );
  if (ids.length === 1) return ids[0];
  // Explicit single-machine group (Asset with name+title children) may repeat
  // the same slot row; total===1 still means one EGM even if ids were empty
  // above because mnum was only on the group (handled by direct).
  if (item.total === 1 && ids.length >= 1) return ids[0];
  return null;
};

/**
 * Whether a play/list row belongs in a SelectedSlots / slotnumbers filter set.
 * Mirrors insightListReport: match mnum OR slot_number/floorLocation so a Search
 * chip that stored floor codes still lights the map (list already dual-keys).
 */
export const rowMatchesSlotFilter = (
  row: CardRow,
  allowed: Set<string> | null | undefined
): boolean => {
  if (!row || !allowed || allowed.size === 0) return false;
  const mnum = part(row.mnum);
  if (mnum && allowed.has(mnum)) return true;
  const sn = part(row.slot_number);
  if (sn && allowed.has(sn)) return true;
  const fl = part(row.floorLocation);
  if (fl && allowed.has(fl)) return true;
  const id = egmDetailId(row);
  return id !== null && allowed.has(String(id));
};

/**
 * Filter play/list rows to a comma-separated slotnumbers / SelectedSlots set.
 * null/empty filter → unchanged. Dual-keys mnum and placement (see rowMatchesSlotFilter).
 */
export const filterRowsBySlotFilter = <T extends { [key: string]: any }>(
  rows: T[] | null | undefined,
  slotFilter: string | null | undefined
): T[] | null | undefined => {
  if (!rows || !slotFilter) return rows;
  const allowed = parseSlotList(slotFilter);
  if (!allowed || allowed.size === 0) return rows;
  return rows.filter((r) => rowMatchesSlotFilter(r, allowed));
};

/**
 * The number shown on the EGM card.
 * Insight: machines.slotNumber already on the row (adaptDoc join — same record as
 * game_name/title); else GetSearchData catalog; else mnum.
 * Never floorLocation/slot_number. Legacy: slot_number.
 */
export const egmCardNumber = (row: CardRow): string => {
  if (!row) return '';
  if (isInsightCardRow(row)) {
    const fromMachinesJoin = part(row.slotNumber);
    if (fromMachinesJoin) return fromMachinesJoin;
    return displayIdForInsightMnum(row.mnum);
  }
  return part(row.slot_number);
};

/** The bank shown after "Bank " on the EGM card. */
export const egmCardBank = (row: CardRow): string => {
  if (!row) return '';
  if (isInsightCardRow(row)) return `${part(row.section)}${part(row.bank)}`;
  return getBankName(
    row.area as string | null | undefined,
    row.section as string | null | undefined
  );
};

/**
 * Full bank label for the EGM card, or '' when an insight row has no bank.
 * Callers render the separator and label only when this is non-empty.
 * Legacy rows keep today's output exactly ("Bank " even with empty area/section).
 */
export const egmCardBankLabel = (row: CardRow): string => {
  const bank = egmCardBank(row);
  if (isInsightCardRow(row) && !bank) return '';
  return `Bank ${bank}`;
};

export interface IEgmCardMeta {
  number: string;
  bankLabel: string;
  /** Separator between number and bank: only when both are present. */
  showSeparator: boolean;
}

/** Everything the card meta line needs, computed once per card. */
export const egmCardMeta = (row: CardRow): IEgmCardMeta => {
  const number = egmCardNumber(row);
  const bankLabel = egmCardBankLabel(row);
  // Legacy rows keep today's separator exactly; insight rows drop it when
  // either side is blank ("Bank C07", not "- Bank C07").
  const showSeparator = isInsightCardRow(row) ? !!number && !!bankLabel : !!bankLabel;
  return { number, bankLabel, showSeparator };
};

/** Insight card name line: API game_name as-is (no clean/split), blank when missing. */
export const insightCardName = (row: CardRow): string => (row ? textOrBlank(row.game_name) : '');

/** Insight card second line: API title as-is, blank when missing. */
export const insightCardTitle = (row: CardRow): string => (row ? textOrBlank(row.title) : '');

/**
 * Value to hand to parseValue for an insight row. The API sends `return` as a
 * PERCENT (44.51); parseValue/formatPercentage expects a fraction and multiplies
 * by 100, so divide here to get the same visual as legacy rows ("45%").
 * Returns null when the value is missing (0 is a real value, kept as 0).
 */
export const insightCardValue = (row: CardRow, insight: string): number | null => {
  if (!row) return null;
  const v = numOrNull(row[insight]);
  if (v === null) return null;
  return insight === 'return' ? v / 100 : v;
};

/**
 * casinoInsight graph/trends rows carry `return` as a PERCENT (44.51). The
 * Details chart, legend and trends table expect the legacy fraction (0.4451)
 * and scale by 100 themselves, so convert once at the service boundary.
 */
const pctToFraction = (v: unknown): unknown => {
  const n = numOrNull(v);
  return n === null ? v : n / 100;
};

/** Graph bar / slot row: return percent -> fraction. */
export const insightGraphRowToFraction = <T>(row: T): T =>
  row && typeof row === 'object' && 'return' in (row as object)
    ? ({ ...row, return: pctToFraction((row as { return?: unknown }).return) } as T)
    : row;

const TREND_VALUE_KEYS = ['m', 'w', 'd', 'h8', 'h4', 'h1'].map((p) => `${p}_insight_value`);

/** Trends row: for insight === 'return', every *_insight_value percent -> fraction. */
export const insightTrendRowToFraction = <T>(row: T): T => {
  // The API sends the insight key string ('return'); a legacy numeric id is
  // mapped through toInsightKey so either form is handled.
  if (!row || typeof row !== 'object') return row;
  const key = (row as { insight?: unknown }).insight;
  const isReturn =
    key === 'return' || ((typeof key === 'number' || typeof key === 'string') && toInsightKey(key) === 'return');
  if (!isReturn) return row;
  const out: Record<string, unknown> = { ...(row as Record<string, unknown>) };
  for (const k of TREND_VALUE_KEYS) if (k in out) out[k] = pctToFraction(out[k]);
  return out as T;
};

/**
 * Card value text. Insight rows with key 'return' take the percent path
 * (API percent -> fraction -> parse; null -> 'N/A', 0 -> '0%'). Every other
 * key and every legacy row keeps the original parse(unparsedValue, key) call,
 * so sleeper/volatility still show their _ntile display.
 */
export const cardInsightDisplay = <R>(
  row: CardRow,
  key: string,
  unparsedValue: unknown,
  parse: (value: any, key: string) => R // eslint-disable-line @typescript-eslint/no-explicit-any
): R | string => {
  if (isInsightCardRow(row) && key === 'return') {
    const v = insightCardValue(row, 'return');
    return v === null ? 'N/A' : parse(v, key);
  }
  return parse(unparsedValue, key);
};

/** Neutral heat (ntile 3) for an insight row whose `${insight}_ntile` is null. */
export const insightNtile = (ntile: number, insight: string, row: CardRow): number => {
  if (!isInsightCardRow(row) || !row) return ntile;
  const raw = row[`${insight}_ntile`];
  return raw === null || raw === undefined || raw === '' ? 3 : ntile;
};

// ---------- insight label on other surfaces ----------

/**
 * One-line insight label: "{displayId} - Bank {section}{bank}", same separator
 * rules as the cards ("Bank C07" without a number, display id without a bank).
 * displayId = displayIdForInsightMnum (matched machines.slotNumber, else mnum).
 */
export const insightLabel = (row: CardRow): string => {
  const { number, bankLabel, showSeparator } = egmCardMeta(row);
  if (showSeparator) return `${number} - ${bankLabel}`;
  return number || bankLabel;
};

/**
 * Search for insight casinos: match display id (machines.slotNumber via catalog),
 * mnum, or raw game_name. Does not match floorLocation / slot_number (placement-only).
 */
export const insightSearchMatches = <T extends Record<string, unknown>>(rows: T[], text: string): T[] => {
  const q = `${text || ''}`.trim().toLowerCase();
  if (!q) return [];
  const seen = new Set<string>();
  return (rows || []).filter((r) => {
    const display = displayIdForInsightMnum(r.mnum).toLowerCase();
    const hit =
      display.includes(q) ||
      part(r.mnum).toLowerCase().includes(q) ||
      textOrBlank(r.game_name).toLowerCase().includes(q);
    if (!hit) return false;
    const key = part(r.mnum) || display;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Map popup/label fields for an insight slot (number = label, raw names). */
export const insightMapLabels = (
  slot: CardRow
): { slotNumber: string; gameTitle: string; gameTheme: string } => ({
  slotNumber: insightLabel(slot),
  gameTitle: insightCardName(slot),
  gameTheme: insightCardTitle(slot),
});

/** Slot copy with `return` percent -> fraction (missing -> undefined) for legacy formatters. */
export const withReturnFraction = <T extends Record<string, unknown>>(slot: T): T => {
  const v = insightCardValue(slot, 'return');
  // returnIsFraction: convertToSlotData must not divide this copy again.
  return { ...slot, return: v === null ? undefined : v, returnIsFraction: true };
};

/** Game Information rows for an insight report: bank = section+bank, slot = display id. */
export const insightGameInfo = (row: CardRow): { bank: string; slotNumber: string } => ({
  bank: egmCardBank(row),
  slotNumber: egmCardNumber(row),
});

/** Details RTP text for insight rows (percent path; 0 -> 0%, missing -> N/A). */
export const withInsightRtp = <S extends { rtp: string }, R>(
  slotData: S,
  row: CardRow,
  parse: (value: any, key: string) => R // eslint-disable-line @typescript-eslint/no-explicit-any
): S => (isInsightCardRow(row) ? { ...slotData, rtp: String(cardInsightDisplay(row, 'return', null, parse)) } : slotData);

/**
 * GetCasinoInsightAdvancedTrends row: insights.return.*_insight_value percent
 * -> fraction, marked as an insight row, and section/bank / placement joined
 * from the summary row with the same mnum (the API pivot omits them).
 *
 * Display id: prefer adaptDoc-joined slotNumber on the summary row (same machines
 * match as game_name); else GetSearchData catalog via egmCardNumber.
 */
export const insightAdvancedRow = <T>(row: T, summaryByMnum?: Map<string, Record<string, unknown>>): T => {
  if (!row || typeof row !== 'object') return row;
  const r = row as Record<string, unknown>;
  const out: Record<string, unknown> = { ...r, isInsightRow: true };
  const insights = r.insights as Record<string, Record<string, unknown>> | undefined;
  if (insights && insights.return) {
    const ret: Record<string, unknown> = { ...insights.return };
    for (const k of TREND_VALUE_KEYS) if (k in ret) ret[k] = pctToFraction(ret[k]);
    out.insights = { ...insights, return: ret };
  }
  const s = summaryByMnum && r.mnum != null ? summaryByMnum.get(String(r.mnum)) : undefined;
  if (s) {
    if (out.section == null) out.section = s.section ?? null;
    if (out.bank == null) out.bank = s.bank ?? null;
    if (out.floorLocation == null) out.floorLocation = s.floorLocation ?? s.slot_number ?? null;
    if (out.slot_number == null) out.slot_number = s.slot_number ?? s.floorLocation ?? null;
    // Same machines join as game_name on GetCasinoInsights summary rows.
    if (out.slotNumber == null && s.slotNumber != null) out.slotNumber = s.slotNumber;
  }
  // Placement aliases stay in sync; display id comes from machines catalog, not insight.
  if (out.floorLocation == null && out.slot_number != null) out.floorLocation = out.slot_number;
  if (out.slot_number == null && out.floorLocation != null) out.slot_number = out.floorLocation;
  return out as T;
};

// ---------- insight card background ----------

/**
 * Optional translucent tint behind Coldest cards (proposal, off by default).
 * Set INSIGHT_COLD_TINT_ENABLED = true to darken LOTC magenta art slightly.
 */
export const INSIGHT_COLD_TINT = 'rgba(29, 33, 46, 0.6)';
export const INSIGHT_COLD_TINT_ENABLED = false;

/** Width of the solid blue bar on the far right of Coldest cards. */
export const COLD_EDGE_WIDTH = '4px';

const COLD_EDGE: Record<number, string> = {
  1: 'rgba(1, 140, 227, 0.5)',
  2: 'rgba(116, 197, 239, 0.5)',
};

/**
 * The inline ntile edge for insight rows was an invalid nested gradient, so
 * the browser dropped it entirely. For Coldest insight rows (ntile 1-2) emit
 * a valid single-layer gradient: transparent card (casino art shows through)
 * with a thin solid blue bar at the far right. Returns undefined to keep the
 * existing style (non-insight, hot, neutral, jackpot rows).
 */
export const insightCardBackground = (
  row: CardRow,
  insight: string,
  ntile: number,
  isJackpotRow: boolean,
  withTint: boolean = INSIGHT_COLD_TINT_ENABLED
): string | undefined => {
  if (!isInsightCardRow(row) || isJackpotRow || JACKPOT_INSIGHTS.includes(insight)) return undefined;
  const n = insightNtile(Number.isFinite(ntile) ? ntile : 3, insight, row);
  const edge = COLD_EDGE[n];
  if (!edge) return undefined;
  const bar = `linear-gradient(90deg, transparent 0%, transparent calc(100% - ${COLD_EDGE_WIDTH}), ${edge} calc(100% - ${COLD_EDGE_WIDTH}), ${edge} 100%)`;
  return withTint ? `${bar}, linear-gradient(${INSIGHT_COLD_TINT}, ${INSIGHT_COLD_TINT})` : bar;
};
